import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import { loadNikkeCatalog } from '../../nikke-catalog/api/nikkeCatalogApi.js';
import { buildNikkeDisplayUpsertPayload } from '../domain/nikkeDbManagement.js';

const NIKKE_MASTER_FUNCTION = 'sync-nikke-master-v6';
const DEFAULT_NIKKE_DATA_API_URL = 'https://dororaid-data.kukla1255.workers.dev';
const NIKKE_DATA_API_URL = String(
  import.meta.env?.VITE_DORORAID_DATA_API_URL || DEFAULT_NIKKE_DATA_API_URL,
).trim().replace(/\/+$/, '');

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function formatFunctionError(payload, fallback = '') {
  const message = String(payload?.message || '').trim();
  const code = String(payload?.code || payload?.error || '').trim();
  if (message && code && !message.includes(code)) return `${message} (${code})`;
  return message || code || fallback;
}

async function readFunctionError(error) {
  try {
    const payload = await error?.context?.json();
    return formatFunctionError(payload, error?.message);
  } catch {
    return error?.message;
  }
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function compactNikkeMasterExport(input) {
  const payload = plainObject(input);
  const rows = Array.isArray(payload.nikkeMaster)
    ? payload.nikkeMaster
    : Array.isArray(payload.nikke_master)
      ? payload.nikke_master
      : [];
  if (!rows.length) throw new Error('니케 마스터 항목이 없는 JSON입니다.');

  return {
    debugger: plainObject(payload.debugger),
    privacy: plainObject(payload.privacy),
    validation: plainObject(payload.validation),
    totals: plainObject(payload.totals),
    nikkeMaster: rows,
    errors: Array.isArray(payload.errors) ? payload.errors : [],
  };
}

async function invokeBody(body) {
  const { data, error } = await requireClient().functions.invoke(NIKKE_MASTER_FUNCTION, { body });
  if (error) throw new Error(await readFunctionError(error) || '니케 마스터 동기화 요청에 실패했습니다.');
  if (!data?.ok) throw new Error(formatFunctionError(data, '니케 마스터 동기화 요청에 실패했습니다.'));
  return data;
}

async function invoke(action, payload = null) {
  return invokeBody(payload ? { action, payload: compactNikkeMasterExport(payload) } : { action });
}

async function currentAccessToken() {
  const { data, error } = await requireClient().auth.getSession();
  if (error) throw error;
  const accessToken = String(data?.session?.access_token || '').trim();
  if (!accessToken) throw new Error('로그인 세션을 확인할 수 없습니다.');
  return accessToken;
}

async function parseDataWorkerResponse(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok || !payload?.ok) {
    throw new Error(formatFunctionError(payload, `Cloudflare D1 동기화 요청에 실패했습니다. (${response.status})`));
  }
  return payload;
}

async function dataWorkerRequest(path, init = {}) {
  if (!NIKKE_DATA_API_URL) throw new Error('Cloudflare 데이터 API 주소가 설정되지 않았습니다.');
  const accessToken = await currentAccessToken();
  const response = await fetch(`${NIKKE_DATA_API_URL}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${accessToken}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  });
  return parseDataWorkerResponse(response);
}

function d1PropagationError(error) {
  const wrapped = new Error(
    `Supabase 공용 DB 반영은 완료됐지만 Cloudflare D1 동기화에 실패했습니다: ${error?.message || '알 수 없는 오류'}`,
  );
  wrapped.code = 'd1_sync_failed_after_supabase_apply';
  wrapped.cause = error;
  return wrapped;
}

export function loadNikkeCatalogD1SyncStatus() {
  return dataWorkerRequest('/v1/admin/nikke/catalog-sync/status');
}

export function syncNikkeCatalogToD1(resourceIds = [], { full = false } = {}) {
  return dataWorkerRequest('/v1/admin/nikke/catalog-sync', {
    method: 'POST',
    body: JSON.stringify({
      full: Boolean(full),
      resourceIds: full ? [] : resourceIds,
    }),
  });
}

export function loadNikkeMasterSyncStatus() {
  return invoke('status');
}

export function previewNikkeMasterIds(resourceIds) {
  return invokeBody({ action: 'fetch_preview', resourceIds });
}

export async function applyNikkeMasterIds(resourceIds) {
  const result = await invokeBody({ action: 'fetch_apply', resourceIds });
  try {
    const d1Sync = await syncNikkeCatalogToD1(resourceIds);
    return { ...result, d1Sync };
  } catch (error) {
    throw d1PropagationError(error);
  }
}

export function previewNikkeMasterExport(payload) {
  return invoke('preview', payload);
}

export async function applyNikkeMasterExport(payload) {
  const result = await invoke('apply', payload);
  try {
    const d1Sync = await syncNikkeCatalogToD1([], { full: true });
    return { ...result, d1Sync };
  } catch (error) {
    throw d1PropagationError(error);
  }
}

export async function saveNikkeCatalogDisplayDraft(item, draft) {
  const payload = buildNikkeDisplayUpsertPayload(item, draft);
  const { data, error } = await requireClient()
    .from('nikke_db')
    .upsert(payload, { onConflict: 'nikke_id' })
    .select('nikke_id,resource_id,name,alias1,alias2,element,burst,image_url,active,updated_at')
    .single();
  if (error) {
    throw new Error(`니케 표시 정보를 저장하지 못했습니다: ${error.message || error.code || 'unknown'}`);
  }

  try {
    const d1Sync = await syncNikkeCatalogToD1([payload.resource_id]);
    await Promise.allSettled([
      loadNikkeCatalog({ includeInactive: true, forceRefresh: true }),
      loadNikkeCatalog({ forceRefresh: true }),
    ]);
    return { item: data, d1Sync };
  } catch (error) {
    throw d1PropagationError(error);
  }
}
